import { Conversation } from '@/types/conversation'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ? `${import.meta.env.VITE_API_BASE_URL}/api/v1` : '/api/v1'

class ApiService {
  private baseUrl: string

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl
  }

  async createConversation(conversationalMode: boolean = true): Promise<Conversation> {
    try {
      const response = await fetch(`${this.baseUrl}/conversations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ conversationalMode }),
      })

      if (!response.ok) {
        const errorText = await response.text().catch(() => response.statusText)
        throw new Error(`Failed to create conversation: ${errorText}`)
      }

      const data = await response.json()
      console.log('createConversation response:', data)
      
      // Handle both response formats
      if (data.conversation) {
        return data.conversation
      } else if (data.conversationId) {
        // Create a basic conversation object with the ID
        return {
          id: data.conversationId,
          messages: [],
          stage: 'initial' as const,
          progress: 0,
          conversationalMode,
        }
      } else {
        throw new Error('Unexpected response format for conversation creation')
      }
    } catch (error) {
      console.error('Error creating conversation:', error)
      throw error
    }
  }

  async sendMessage(conversationId: string, message: string): Promise<any> {
    try {
      console.log('Sending message to:', `${this.baseUrl}/conversations/${conversationId}/message`)
      console.log('Message preview:', message.substring(0, 100) + '...')
      
      const response = await fetch(`${this.baseUrl}/conversations/${conversationId}/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message }),
      })

      if (!response.ok) {
        const errorText = await response.text().catch(() => response.statusText)
        throw new Error(`Failed to send message: ${errorText}`)
      }

      const result = await response.json()
      console.log('sendMessage raw response:', result)
      
      // Handle wrapped response format
      if (result.success && result.data) {
        console.log('Message response data:', {
          hasResponse: !!result.data.response,
          hasBpmn: !!result.data.bpmn,
          stage: result.data.stage,
          progress: result.data.progress
        })
        return result.data
      }
      
      // Fallback for direct response
      if (result.response) {
        return result
      }
      
      throw new Error('Unexpected message response format')
    } catch (error) {
      console.error('Error sending message:', error)
      throw error
    }
  }

  async getConversation(conversationId: string): Promise<Conversation> {
    try {
      const response = await fetch(`${this.baseUrl}/conversations/${conversationId}`)

      if (!response.ok) {
        throw new Error(`Failed to get conversation: ${response.statusText}`)
      }

      const result = await response.json()
      console.log('getConversation raw response:', result)
      
      // Handle wrapped response format consistently
      if (result.success && result.data) {
        const conversation = result.data
        console.log('Extracted conversation data:', {
          id: conversation.id,
          messagesCount: conversation.messages?.length,
          hasBpmn: !!conversation.bpmn,
          stage: conversation.stage,
          progress: conversation.progress
        })
        return conversation as Conversation
      }
      
      // Fallback for direct conversation object
      if (result.id) {
        return result as Conversation
      }
      
      throw new Error('Unexpected conversation response format')
    } catch (error) {
      console.error('Error fetching conversation:', error)
      throw error
    }
  }

  async getBPMN(conversationId: string, format: 'xml' | 'json' = 'xml'): Promise<string> {
    try {
      const response = await fetch(
        `${this.baseUrl}/conversations/${conversationId}/bpmn?format=${format}`
      )

      if (!response.ok) {
        const errorText = await response.text().catch(() => response.statusText)
        throw new Error(`Failed to get BPMN: ${errorText}`)
      }

      if (format === 'xml') {
        return response.text()
      }

      const data = await response.json()
      return data.bpmn || ''
    } catch (error) {
      console.error('Error fetching BPMN:', error)
      throw error
    }
  }

  async getAuditTrail(conversationId: string, summary: boolean = false): Promise<any> {
    try {
      const url = summary
        ? `${this.baseUrl}/conversations/${conversationId}/audit?summary=true`
        : `${this.baseUrl}/conversations/${conversationId}/audit`

      const response = await fetch(url)

      if (!response.ok) {
        const errorText = await response.text().catch(() => response.statusText)
        throw new Error(`Failed to get audit trail: ${errorText}`)
      }

      return response.json()
    } catch (error) {
      console.error('Error fetching audit trail:', error)
      throw error
    }
  }

  async getBlocks(): Promise<any[]> {
    const response = await fetch(`${this.baseUrl}/blocks`)

    if (!response.ok) {
      throw new Error(`Failed to get blocks: ${response.statusText}`)
    }

    const data = await response.json()
    return data.blocks || []
  }

  async getStatus(): Promise<any> {
    const response = await fetch(`${this.baseUrl}/status`)

    if (!response.ok) {
      throw new Error(`Failed to get status: ${response.statusText}`)
    }

    return response.json()
  }
}

// Export singleton instance
export const apiService = new ApiService()